import type { LoadPlanningResponseDto } from '@/src/api/types/procurement'
import { useLocationsContext } from '@/src/contexts/LocationsContext'
import { ALL_LOCATIONS } from '@/src/contexts/LocationsContext'
import { GranularityEnum } from '@/src/enums/granularity.enum'
import { mapToEnergyTypeDto } from '@/src/features/procurement-and-markets/details/types/adjustment.utils'
import KPIBoxInfo from '@/src/features/procurement-and-markets/load-planning/components/KPIBoxInfo'
import { useLoadPlanningCurrentLocation } from '@/src/features/procurement-and-markets/load-planning/contexts/LoadPlanningCurrentLocationContext'
import {
  useLoadPlanningCurve,
  useLoadedStrategyMetadata,
} from '@/src/features/procurement-and-markets/load-planning/hooks/LoadPlanning.hooks'
import { FlexRow } from '@components'
import { useTranslation } from 'react-i18next'

const calculateKpis = (forecastCurveData?: LoadPlanningResponseDto) => {
  if (!forecastCurveData || !forecastCurveData.kpis) return null
  const { kpis, monthlyData } = forecastCurveData

  const shouldCalculateDraftKpi = monthlyData.some(data => !!data.draftValue)

  const { aggregatedDraftConsumption, plannedConsumption, referencePeriodConsumption } = kpis

  const savedComparisonAgainstReferenceDiff = plannedConsumption.energy.amount - referencePeriodConsumption.energy.amount
  const draftComparisonAgainstSavedDiff = aggregatedDraftConsumption.energy.amount - plannedConsumption.energy.amount

  return {
    saved: {
      consumption: plannedConsumption.energy.amount,
    },
    savedComparisonAgainstReference: {
      diff: savedComparisonAgainstReferenceDiff,
      percentage: (savedComparisonAgainstReferenceDiff / referencePeriodConsumption.energy.amount) * 100,
    },
    draft: shouldCalculateDraftKpi
      ? {
          consumption: aggregatedDraftConsumption,
          percentage: (draftComparisonAgainstSavedDiff / plannedConsumption.energy.amount) * 100,
        }
      : undefined,
  }
}

export const LoadPlanningKpiBoxes = () => {
  const { t } = useTranslation()
  const { data: strategyMetadata } = useLoadedStrategyMetadata()
  const { companyPgId: companyId } = useLocationsContext()
  const { locationId } = useLoadPlanningCurrentLocation()

  const { data: loadForecastData } = useLoadPlanningCurve({
    companyId,
    locationId: locationId !== ALL_LOCATIONS ? locationId : undefined,
    year: strategyMetadata?.year,
    energyType: mapToEnergyTypeDto(strategyMetadata?.energyType),
    granularity: GranularityEnum.ONE_MONTH,
  })

  const kpis = calculateKpis(loadForecastData)

  return (
    <FlexRow gap={4}>
      <KPIBoxInfo
        infoText={t('components.procurement.forecasting.graph.saved_load')}
        kpiValue={kpis?.saved.consumption}
        tooltipText={t('components.procurement.forecasting.graph.saved_load_tooltip')}
        hidePercentage={true}
      />
      <KPIBoxInfo
        infoText={t('components.procurement.forecasting.graph.saved_comparison_against_reference')}
        kpiValue={kpis?.savedComparisonAgainstReference.diff}
        percentageValue={kpis?.savedComparisonAgainstReference.percentage}
        tooltipText={t('components.procurement.forecasting.graph.saved_comparison_against_reference_tooltip')}
        hidePercentage={!kpis}
      />
      <KPIBoxInfo
        infoText={t('components.procurement.forecasting.graph.draft_comparison_against_saved')}
        kpiValue={kpis?.draft?.consumption.energy.amount}
        percentageValue={kpis?.draft?.percentage}
        tooltipText={t('components.procurement.forecasting.graph.draft_comparison_against_saved_tooltip')}
        hidePercentage={!kpis?.draft}
      />
    </FlexRow>
  )
}
